import {Injectable} from '@angular/core';
import {AnalyticsFactInput, FilterValues, VehicleTransaction} from '../../models/vte';
import {TimelineActivity} from '../../shared/activity-timeline/activity-timeline.component';
import {VehicleTransactionListParams} from './vte-api.service';
import {OperationsFiltersService} from './operations-filters.service';
import {OperationsOrganizationService} from './operations-organization.service';
import {
    anchorCreatedLifecycleHelp,
    anchorTimeHelp,
    CREATED_TIME_HELP,
    customerAnalyticsName,
    formatProviderLabel
} from './vision-customer-labels';
import {VisionConfigService} from './vision-config.service';
import {AuthService} from '../auth.service';
import {resolveTransactionStatusBadge} from './vte.constants';
import {
    ANALYTICS_FACT_NOT_ENABLED,
    isProviderExpected,
    isVerificationFact,
    resolveAnalyticsFactValue
} from '../../shared/vte/analytics-fact.utils';
import {ReportingDateTimeService} from '../../shared/datetime/reporting-datetime.service';

export type MetadataEntry = {
    key: string;
    label: string;
    value: string;
    help?: string;
    routerLink?: string[];
    queryParams?: Record<string, string>;
};

export type RelatedRecordLink = {
    key: string;
    label: string;
    description: string;
    routerLink: string[];
    queryParams?: Record<string, string>;
};

@Injectable({
    providedIn: 'root',
})
export class VehicleTransactionViewService {
    constructor(
        private visionConfig: VisionConfigService,
        private filtersService: OperationsFiltersService,
        private organizationService: OperationsOrganizationService,
        private auth: AuthService,
        private reportingDateTime: ReportingDateTimeService,
    ) {
    }

    buildListParams(
        filters: FilterValues,
        page: number,
        pageSize: number,
        ordering: string,
    ): VehicleTransactionListParams {
        return {
            page,
            page_size: pageSize,
            ordering: ordering || '-anchor_timestamp',
            search: this.filtersService.getFilterString(filters, 'search'),
            status_group: this.filtersService.getFilterString(filters, 'status_group'),
            camera_id: this.filtersService.getFilterString(filters, 'camera_id'),
            org_id: this.organizationService.resolveOrganizationId(
                this.filtersService.getFilterString(filters, 'org_id'),
            ),
        };
    }

    buildMetadataEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const showImplementation = this.auth.isSuperUser();
        const entries: MetadataEntry[] = [
            {key: 'transaction_id', label: 'Transaction ID', value: transaction.transaction_id},
            {key: 'organization_id', label: 'Organization', value: String(transaction.organization_id)},
            {
                key: 'status',
                label: 'Status',
                value: resolveTransactionStatusBadge(transaction.status)?.label || transaction.status,
            },
            {
                key: 'anchor_timestamp',
                label: 'Anchor Time',
                value: this.formatTimestamp(transaction.anchor_timestamp),
                help: anchorTimeHelp(showImplementation),
            },
        ];

        if (transaction.correlated_plate) {
            entries.push({key: 'plate', label: 'Vehicle Number', value: transaction.correlated_plate});
        }
        if (transaction.correlated_direction) {
            entries.push({key: 'direction', label: 'Direction', value: transaction.correlated_direction});
        }
        if (transaction.camera_id != null) {
            entries.push({key: 'camera_id', label: 'Camera ID', value: String(transaction.camera_id)});
        }
        if (transaction.correlation_window_seconds != null) {
            entries.push({
                key: 'correlation_window',
                label: 'Correlation Window',
                value: `${transaction.correlation_window_seconds}s`,
            });
        }
        if (transaction.created_at) {
            entries.push({
                key: 'created_at',
                label: 'Created',
                value: this.formatTimestamp(transaction.created_at),
                help: CREATED_TIME_HELP
            });
        }
        if (transaction.updated_at) {
            entries.push({key: 'updated_at', label: 'Updated', value: this.formatTimestamp(transaction.updated_at)});
        }

        return entries;
    }

    buildTimeline(transaction: VehicleTransaction): TimelineActivity[] {
        const activities: TimelineActivity[] = [];

        if (transaction.created_at) {
            activities.push({
                title: 'Transaction created',
                created_at: transaction.created_at,
            });
        }
        if (transaction.wait_started_at) {
            activities.push({
                title: 'Waiting for provider events',
                created_at: transaction.wait_started_at,
            });
        }

        const stickerActivity = this.buildStickerTimelineActivity(transaction);
        if (stickerActivity) {
            activities.push(stickerActivity);
        }

        if (transaction.timed_out_at) {
            activities.push({
                title: 'Correlation timed out',
                created_at: transaction.timed_out_at,
            });
        }
        if (transaction.ready_for_inspection_at) {
            activities.push({
                title: 'Ready for inspection',
                created_at: transaction.ready_for_inspection_at,
            });
        }
        if (transaction.status === 'INSPECTION_COMPLETED' || transaction.status === 'ACTIONS_EXECUTED' || transaction.status === 'COMPLETED') {
            activities.push({
                title: 'Inspection completed',
                created_at: transaction.ready_for_inspection_at || transaction.updated_at || null,
            });
        }
        if (transaction.status === 'ACTIONS_EXECUTED' || transaction.status === 'COMPLETED') {
            activities.push({
                title: 'Actions executed',
                created_at: transaction.updated_at || null,
            });
        }
        if (transaction.status === 'COMPLETED') {
            activities.push({
                title: 'Transaction completed',
                created_at: transaction.updated_at || null,
            });
        }

        return activities;
    }

    private buildStickerTimelineActivity(transaction: VehicleTransaction): TimelineActivity | null {
        if (!this.visionConfig.shouldShowAnalyticsKey('sticker_verification')) {
            return null;
        }
        const definition = this.visionConfig.getDefinitionByAnalyticsKey('sticker_verification');
        const outputFact = definition?.output_fact || 'sticker_present';
        const correlatedFacts = transaction.correlated_facts || {};
        const analyticsContext = transaction.pipeline?.analytics_context || {};
        const rawValue = correlatedFacts[outputFact] ?? analyticsContext[outputFact] ?? null;
        const capabilityExpected = isProviderExpected(
            transaction.expected_provider_events,
            definition?.provider || 'eeva',
        );
        const resolved = resolveAnalyticsFactValue(
            rawValue as AnalyticsFactInput['value'],
            null,
            {
                analyticsKey: 'sticker_verification',
                outputFact,
                showPendingWhenEmpty: true,
                capabilityExpected,
            },
        );
        return {
            title: resolved?.displayValue || (capabilityExpected ? 'Verification Pending' : ANALYTICS_FACT_NOT_ENABLED),
            created_at: transaction.ready_for_inspection_at
                || transaction.updated_at
                || transaction.created_at
                || null,
        };
    }

    getCurrentTimelineIndex(transaction: VehicleTransaction, activities: TimelineActivity[]): number {
        if (!activities.length) {
            return -1;
        }
        if (transaction.status === 'COMPLETED') {
            return activities.length - 1;
        }
        return activities.length - 1;
    }

    buildAnalyticsFacts(transaction: VehicleTransaction): AnalyticsFactInput[] {
        const facts: AnalyticsFactInput[] = [];
        const correlatedFacts = transaction.correlated_facts || {};
        const analyticsContext = transaction.pipeline?.analytics_context || {};

        for (const definition of this.visionConfig.enabledAnalytics) {
            const outputFact = definition.output_fact?.trim();
            if (!outputFact || !this.visionConfig.shouldShowFact(outputFact)) {
                continue;
            }

            const capabilityExpected = isProviderExpected(
                transaction.expected_provider_events,
                definition.provider,
            );
            const rawValue =
                correlatedFacts[outputFact]
                ?? analyticsContext[outputFact]
                ?? null;

            // Keep verification capabilities visible (Pending or Not Enabled) when no fact yet.
            if (
                (rawValue === null || rawValue === undefined)
                && !isVerificationFact(definition.analytics_key, outputFact)
            ) {
                continue;
            }

            facts.push({
                analytics_key: definition.analytics_key,
                output_fact: outputFact,
                label: this.auth.isSuperUser() ? definition.name : customerAnalyticsName(definition),
                value: (rawValue ?? null) as AnalyticsFactInput['value'],
                definition,
                capabilityExpected,
            });
        }

        return facts;
    }

    getInspectionOutcome(transaction: VehicleTransaction): string | null {
        const inspection = transaction.pipeline?.inspection;
        if (!inspection || typeof inspection !== 'object') {
            return null;
        }
        const outcome = (inspection as Record<string, unknown>)['outcome']
            ?? (inspection as Record<string, unknown>)['inspection_outcome'];
        return typeof outcome === 'string' ? outcome : null;
    }

    getAccessDecision(transaction: VehicleTransaction): string | null {
        const inspection = transaction.pipeline?.inspection;
        if (!inspection || typeof inspection !== 'object') {
            return null;
        }
        const decision = (inspection as Record<string, unknown>)['access_decision'];
        return typeof decision === 'string' ? decision : null;
    }

    buildVehicleLogMetadataEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const metadata = transaction.pipeline?.vehicle_log_metadata;
        if (!metadata || typeof metadata !== 'object') {
            return [];
        }
        return Object.entries(metadata).map(([key, value]) => ({
            key,
            label: this.humanizeKey(key),
            value: this.stringifyValue(value),
        }));
    }

    buildInspectionEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const inspection = transaction.pipeline?.inspection;
        if (!inspection || typeof inspection !== 'object') {
            return [];
        }
        return Object.entries(inspection as Record<string, unknown>).map(([key, value]) => ({
            key,
            label: this.humanizeKey(key),
            value: this.stringifyValue(value),
        }));
    }

    buildOperationalDiagnosticsEntries(transaction: VehicleTransaction): MetadataEntry[] {
        return [
            ...this.buildTransactionSummaryEntries(transaction),
            ...this.buildCorrelationEntries(transaction),
            ...this.buildTimingEntries(transaction),
        ];
    }

    buildTransactionSummaryEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const showImplementation = this.auth.isSuperUser();
        const stage = resolveTransactionStatusBadge(transaction.status);
        const inspectionOutcome = this.getInspectionOutcome(transaction);
        const accessDecision = this.getAccessDecision(transaction);
        const failureReason = this.resolveFailureReason(transaction, showImplementation);

        const entries: MetadataEntry[] = [
            {
                key: 'status',
                label: 'Status',
                value: stage?.label || transaction.status,
            },
            {
                key: 'current_stage',
                label: 'Current Stage',
                value: stage?.label || transaction.status,
            },
        ];

        if (failureReason) {
            entries.push({key: 'failure_reason', label: 'Failure Reason', value: failureReason});
        }

        entries.push({
            key: 'inspection_result',
            label: 'Inspection Result',
            value: inspectionOutcome || 'Pending',
        });
        entries.push({
            key: 'decision',
            label: 'Decision',
            value: accessDecision || 'Pending',
        });

        return entries;
    }

    buildCorrelationEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const showImplementation = this.auth.isSuperUser();
        const expectedIds = this.getExpectedProviderIds(transaction);
        const receivedIds = transaction.received_provider_ids || [];
        const timedOutIds = this.getTimedOutProviders(transaction);
        const missingIds = expectedIds.filter(
            (providerId) => !receivedIds.includes(providerId) && !timedOutIds.includes(providerId),
        );

        return [
            {
                key: 'expected_providers',
                label: 'Expected Providers',
                value: this.formatExpectedProviders(transaction.expected_provider_events, showImplementation),
            },
            {
                key: 'received_providers',
                label: 'Received Providers',
                value: this.formatProviderIdList(receivedIds, showImplementation),
            },
            {
                key: 'missing_providers',
                label: 'Missing Providers',
                value: this.formatProviderIdList(missingIds, showImplementation),
            },
            {
                key: 'timed_out_providers',
                label: 'Timed Out Providers',
                value: timedOutIds.length
                    ? this.formatProviderIdList(timedOutIds, showImplementation)
                    : '—',
            },
        ];
    }

    buildTimingEntries(transaction: VehicleTransaction): MetadataEntry[] {
        const showImplementation = this.auth.isSuperUser();
        const providerEventTime = this.getEarliestProviderEventTime(transaction);
        const completedTime = transaction.status === 'COMPLETED'
            ? (transaction.updated_at || null)
            : null;

        return [
            {
                key: 'anchor_timestamp',
                label: 'Anchor Time',
                value: this.formatTimestamp(transaction.anchor_timestamp),
                help: anchorTimeHelp(showImplementation),
            },
            {
                key: 'provider_event_time',
                label: 'Provider Event Time',
                value: providerEventTime ? this.formatTimestamp(providerEventTime) : '—',
                help: anchorCreatedLifecycleHelp(showImplementation),
            },
            {
                key: 'created_at',
                label: 'Transaction Created',
                value: this.formatTimestamp(transaction.created_at),
                help: CREATED_TIME_HELP,
            },
            {
                key: 'completed_time',
                label: 'Completed Time',
                value: completedTime ? this.formatTimestamp(completedTime) : '—',
            },
            {
                key: 'total_processing_time',
                label: 'Total Processing Time',
                value: this.formatProcessingDuration(transaction),
            },
        ];
    }

    buildRelatedRecordLinks(transaction: VehicleTransaction): RelatedRecordLink[] {
        const links: RelatedRecordLink[] = [];
        const inspectionOutcome = this.getInspectionOutcome(transaction);
        const accessDecision = this.getAccessDecision(transaction);

        if (transaction.pipeline?.vehicle_log_id || transaction.pipeline?.vehicle_log_metadata) {
            links.push({
                key: 'vehicle_log',
                label: 'Vehicle Log',
                description: this.resolveVehicleLogStatus(transaction),
                routerLink: ['/track-vehicle'],
            });
        }

        for (const link of transaction.provider_event_links || []) {
            links.push({
                key: `provider_event_${link.id}`,
                label: 'Provider Event',
                description: `${link.provider_event_id}`,
                routerLink: ['/operations/provider-events', String(link.id)],
                queryParams: {provider_id: link.provider_id},
            });
        }

        if (inspectionOutcome) {
            links.push({
                key: 'inspection_result',
                label: 'Inspection Result',
                description: inspectionOutcome,
                routerLink: ['/operations/transactions', transaction.transaction_id],
            });
        }

        if (accessDecision) {
            links.push({
                key: 'decision',
                label: 'Decision',
                description: accessDecision,
                routerLink: ['/operations/transactions', transaction.transaction_id],
            });
        }

        return links;
    }

    private getExpectedProviderIds(transaction: VehicleTransaction): string[] {
        const expected = transaction.expected_provider_events || [];
        return expected
            .map((item) => {
                if (!item || typeof item !== 'object') {
                    return '';
                }
                return String((item as Record<string, unknown>)['provider_id'] || '');
            })
            .filter(Boolean);
    }

    private getEarliestProviderEventTime(transaction: VehicleTransaction): string | null {
        const facts = transaction.correlated_facts || {};
        let earliest: string | null = null;
        for (const value of Object.values(facts)) {
            if (!value || typeof value !== 'object') {
                continue;
            }
            const timestamp = (value as Record<string, unknown>)['provider_timestamp'];
            if (typeof timestamp !== 'string' || !timestamp) {
                continue;
            }
            if (!earliest || new Date(timestamp).getTime() < new Date(earliest).getTime()) {
                earliest = timestamp;
            }
        }
        return earliest;
    }

    private formatProcessingDuration(transaction: VehicleTransaction): string {
        const start = transaction.anchor_timestamp || transaction.created_at;
        const end = transaction.status === 'COMPLETED'
            ? transaction.updated_at
            : transaction.updated_at || transaction.ready_for_inspection_at;
        if (!start || !end) {
            return '—';
        }
        const durationMs = new Date(end).getTime() - new Date(start).getTime();
        if (Number.isNaN(durationMs) || durationMs < 0) {
            return '—';
        }
        const totalSeconds = Math.round(durationMs / 1000);
        if (totalSeconds < 60) {
            return `${totalSeconds}s`;
        }
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }

    private resolveFailureReason(transaction: VehicleTransaction, showImplementation: boolean): string | null {
        const timedOut = this.getTimedOutProviders(transaction);
        if (timedOut.length) {
            return `Provider correlation timeout: ${this.formatProviderIdList(timedOut, showImplementation)}`;
        }

        const inspectionOutcome = (this.getInspectionOutcome(transaction) || '').toUpperCase();
        if (inspectionOutcome === 'MANUAL_REVIEW') {
            return 'Manual approval required';
        }
        if (inspectionOutcome === 'WARNING') {
            return 'Inspection warning — review recommended';
        }
        if (inspectionOutcome === 'FAIL') {
            return 'Inspection policy returned FAIL';
        }

        if (transaction.status === 'READY_FOR_INSPECTION_POLICY' && !this.getInspectionOutcome(transaction)) {
            return 'Inspection or decision pending';
        }

        const metadata = transaction.correlation_metadata || {};
        const reason = metadata['failure_reason'] || metadata['error'];
        return typeof reason === 'string' && reason.trim() ? reason : null;
    }

    private getTimedOutProviders(transaction: VehicleTransaction): string[] {
        const metadata = transaction.correlation_metadata || {};
        const timedOut = metadata['timed_out_providers'];
        return Array.isArray(timedOut) ? timedOut.map((value) => String(value)) : [];
    }

    private formatExpectedProviders(
        expected: unknown[] | null | undefined,
        showImplementation: boolean,
    ): string {
        if (!expected?.length) {
            return '—';
        }
        return expected
            .map((item) => {
                if (!item || typeof item !== 'object') {
                    return null;
                }
                const providerId = String((item as Record<string, unknown>)['provider_id'] || '');
                if (!providerId) {
                    return null;
                }
                const timeout = (item as Record<string, unknown>)['expected_event_timeout_seconds'];
                const label = formatProviderLabel(providerId, showImplementation);
                return timeout != null ? `${label} (${timeout}s)` : label;
            })
            .filter(Boolean)
            .join(', ') || '—';
    }

    private formatProviderIdList(
        providerIds: string[] | null | undefined,
        showImplementation: boolean,
    ): string {
        if (!providerIds?.length) {
            return '—';
        }
        return providerIds
            .map((providerId) => formatProviderLabel(providerId, showImplementation))
            .join(', ');
    }

    private resolveVehicleLogStatus(transaction: VehicleTransaction): string {
        const metadata = transaction.pipeline?.vehicle_log_metadata;
        if (!metadata || typeof metadata !== 'object' || !Object.keys(metadata).length) {
            return 'Not derived';
        }
        const outcome = (metadata as Record<string, unknown>)['vte_inspection_outcome'];
        if (typeof outcome === 'string' && outcome.trim()) {
            return `Derived (${outcome})`;
        }
        return 'Derived';
    }

    private formatProviderTimestamps(
        transaction: VehicleTransaction,
        showImplementation: boolean,
    ): string | null {
        const facts = transaction.correlated_facts || {};
        const parts: string[] = [];
        for (const [providerId, value] of Object.entries(facts)) {
            if (!value || typeof value !== 'object') {
                continue;
            }
            const timestamp = (value as Record<string, unknown>)['provider_timestamp'];
            if (!timestamp) {
                continue;
            }
            const label = formatProviderLabel(providerId, showImplementation);
            parts.push(`${label}: ${this.formatTimestamp(String(timestamp))}`);
        }
        return parts.length ? parts.join(' · ') : null;
    }

    private formatTimestamp(value: string | null | undefined): string {
        if (!value) {
            return '—';
        }
        const formatted = this.reportingDateTime.format(value);
        return formatted || value;
    }

    private humanizeKey(key: string): string {
        return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }

    private stringifyValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }
}
